import React, { useId } from 'react';

/**
 * AccordionItem — adapted from the design reference.
 *
 * DEPARTURES:
 *
 * 1. PANEL WIRING. The reference sets aria-expanded on the trigger but never
 *    associates it with the panel, so a screen reader is told something expands
 *    and not what. aria-controls / role="region" / aria-labelledby added.
 *
 * 2. CHILDREN. The reference hardcodes a <p>, which breaks any FAQ answer
 *    containing a list or a link — and the /ux-audit pillar's FAQPage schema is
 *    built from exactly this content. Renders a <div> so children are free.
 *
 * 3. PANEL STAYS MOUNTED, hidden via [hidden]. Unmounting the answer removes it
 *    from the DOM, and the FAQ content is SEO-bearing (FAQPage JSON-LD on the
 *    pillar page). Crawlers should see the answer text without interaction.
 */

export function AccordionItem({
  question,
  children,
  open = false,
  onToggle,
  className = '',
  style,
}) {
  const id = useId();
  const panelId = `${id}-panel`;
  const triggerId = `${id}-trigger`;

  return (
    <div
      className={`ds-accordion-item ${className}`.trim()}
      style={{ borderBottom: '1px solid var(--border-1)', ...style }}
    >
      <button
        type="button"
        id={triggerId}
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={panelId}
        style={{
          all: 'unset',
          boxSizing: 'border-box',
          cursor: 'pointer',
          width: '100%',
          minHeight: 'var(--target-min)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 20,
          padding: '22px 4px',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--type-body-size)',
          fontWeight: 500,
          color: 'var(--ink-1)',
        }}
      >
        {question}
        {/* WAS --ink-3, measured 3.42:1 at 18px on the built page — an AA
            failure on the one glyph that says whether the panel is open.
            --accent-text (#B3320A, 6.02:1) is the text-safe orange and is what
            the artboard draws here; --accent (#FF5C2B) is banned as text. */}
        <span aria-hidden="true" style={{ color: 'var(--accent-text)', fontSize: 18, flex: 'none' }}>
          {open ? '−' : '+'}
        </span>
      </button>

      <div
        id={panelId}
        role="region"
        aria-labelledby={triggerId}
        hidden={!open}
        style={{
          margin: 0,
          padding: '0 4px 24px',
          maxWidth: 520,
          fontSize: 14,
          lineHeight: 1.7,
          color: 'var(--ink-3)',
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default AccordionItem;
